import React, { useEffect, useCallback, useState } from 'react'
import cn from 'classnames'
import StreamEmbed from '../shared/StreamEmbed'
import SearchHeader from './SearchHeader'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { useGetStreams } from 'refactor/hooks/api/useGetStreams'
import queryClient from 'api/queryClient'
import styles from './streams.module.scss'
import panelStyles from './panel.module.scss'

interface StreamsProps {
    filter?: any
}

function Loading() {
    return (
        <div className={panelStyles.loader}>
            <div className={cn(panelStyles.loadingIcon)}>
                <i className="fa fa-circle-o-notch fa-spin fa-lg" />
            </div>
        </div>
    )
}

const Streams: React.FC<StreamsProps> = ({ filter }) => {
    const { panel, scrollContainerRef, setIsRefreshing, isRefreshing } = usePanelContext()
    const [isLocalRefreshing, setLocalIsRefreshing] = useState(false)

    // Use the shared refresh state if available, otherwise fallback to local state
    const handleSetIsRefreshing = useCallback(
        (value: boolean) => {
            if (setIsRefreshing) {
                setIsRefreshing(value)
            } else {
                setLocalIsRefreshing(value)
            }
        },
        [setIsRefreshing],
    )

    // Use React Query hook for fetching streams
    const {
        streams,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isLoading,
        refetch,
    } = useGetStreams()

    // Handle initial mount
    useEffect(() => {
        // Invalidate and refetch streams when the panel mounts
        queryClient.refetchQueries({ queryKey: ['streams'], exact: true })
    }, [])

    // Handle scroll for infinite loading
    useEffect(() => {
        const scrollContainer = scrollContainerRef?.current
        if (!scrollContainer) return

        const onScroll = () => {
            if (scrollContainer) {
                const { scrollTop, scrollHeight, clientHeight } = scrollContainer
                if (
                    scrollTop + clientHeight >= scrollHeight - 500 &&
                    hasNextPage &&
                    !isFetchingNextPage
                ) {
                    fetchNextPage()
                }
            }
        }

        scrollContainer.addEventListener('scroll', onScroll)

        return () => {
            scrollContainer.removeEventListener('scroll', onScroll)
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, scrollContainerRef])

    const formatLastActivity = (timestamp?: string) => {
        if (!timestamp) return 'No recent activity'
        try {
            const date = new Date(timestamp)
            const now = new Date()
            const diffMs = now.getTime() - date.getTime()
            const diffMins = Math.floor(diffMs / 60000)
            const diffHours = Math.floor(diffMins / 60)
            const diffDays = Math.floor(diffHours / 24)

            if (diffDays > 0) {
                return `Active ${diffDays} day${diffDays > 1 ? 's' : ''} ago`
            } else if (diffHours > 0) {
                return `Active ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
            } else if (diffMins > 0) {
                return `Active ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
            } else {
                return 'Active just now'
            }
        } catch {
            return 'No recent activity'
        }
    }

    // Handle pull-to-refresh
    useEffect(() => {
        if (isRefreshing || (setIsRefreshing && isLocalRefreshing)) {
            console.log('Streams - refreshing content, isRefreshing:', isRefreshing)

            // Add minimum refresh time for better UX
            const startTime = Date.now()
            refetch().then(() => {
                const elapsed = Date.now() - startTime
                const remainingTime = Math.max(0, 800 - elapsed) // Minimum 800ms refresh to match other panels

                setTimeout(() => {
                    console.log('Streams - refresh complete')
                    handleSetIsRefreshing(false)
                }, remainingTime)
            })
        }
    }, [isRefreshing, isLocalRefreshing, refetch, handleSetIsRefreshing])

    return (
        <div className={panelStyles.questStreamComp}>
            <div className={panelStyles.searchHeaderContainer}>
                <SearchHeader filter={filter} setIsRefreshing={handleSetIsRefreshing} />
            </div>

            {isLoading || (isFetching && !streams.length) ? (
                <Loading />
            ) : streams.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No public streams found</p>
                </div>
            ) : (
                <div className={styles.streamsContainer}>
                    {streams.map(stream => (
                        <StreamEmbed
                            key={stream.id}
                            stream={stream}
                            lastActivity={formatLastActivity(stream.last_activity)}
                            questCount={stream.quest_count}
                        />
                    ))}
                    {isFetchingNextPage && (
                        <div className={panelStyles.loader}>
                            <div className={cn(panelStyles.loadingIcon)}>
                                <i className="fa fa-circle-o-notch fa-spin fa-lg" />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default Streams
